import { useEffect, useRef, useState } from 'react'
import { BUILDER_ID } from '../data/builder'

const SESSION_KEY = 'gritvue-objection-shown'
/** After a dismiss, stay quiet for returning visitors for this long. */
const SNOOZE_KEY = 'gritvue-objection-snoozed-until'
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000
const TIME_ON_PAGE_MS = 60_000
const SCROLL_THRESHOLD = 0.35
/** Coming back to pricing or specs this many times without choosing also counts as engagement. */
const REVISITS_NEEDED = 2

function snoozed(): boolean {
  try {
    return Number(window.localStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now()
  } catch {
    return false
  }
}

function snooze() {
  try {
    window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
  } catch {
    /* storage unavailable: the session flag still prevents repeats */
  }
}

function builderOnScreen(): boolean {
  const el = document.getElementById(BUILDER_ID)
  if (!el) return false
  const rect = el.getBoundingClientRect()
  return rect.top < window.innerHeight && rect.bottom > 0
}

/**
 * The "Still deciding?" helper stays available, never pushy:
 * - Waits 60 seconds AND a meaningful scroll (or repeated returns to pricing/specs).
 * - Never while the builder is on screen or after any builder interaction or buy click.
 * - Never while another dialog is open.
 * - Appears first as a small corner button; it only expands when tapped.
 * - Once per session; dismissing also snoozes it for 3 days.
 */
export function useObjectionTrigger() {
  const [open, setOpen] = useState(false)
  const [teaser, setTeaser] = useState(false)
  const [timeReady, setTimeReady] = useState(false)
  const [scrollReady, setScrollReady] = useState(false)
  const [revisits, setRevisits] = useState(0)
  const [hasIntent, setHasIntent] = useState(false)
  const [done, setDone] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.sessionStorage.getItem(SESSION_KEY) === '1' || snoozed()
  })
  const entered = useRef<Record<string, number>>({})

  useEffect(() => {
    const timer = window.setTimeout(() => setTimeReady(true), TIME_ON_PAGE_MS)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      if (window.scrollY / scrollable >= SCROLL_THRESHOLD) setScrollReady(true)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Count every fresh entry into pricing or specs; the second one is a revisit.
  useEffect(() => {
    const targets = ['offer', 'specs'].map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[]
    if (!targets.length) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const id = entry.target.id
          entered.current[id] = (entered.current[id] ?? 0) + 1
          if (entered.current[id] >= REVISITS_NEEDED) setRevisits((count) => count + 1)
        })
      },
      { threshold: 0.2 },
    )
    targets.forEach((target) => observer.observe(target))
    return () => observer.disconnect()
  }, [])

  // Any move toward buying, or any touch of the builder, disables it for the session.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const el = event.target as HTMLElement | null
      if (!el) return
      if (el.closest('[data-buy-intent], [data-builder], [id^="pack-"][id$="-cta"]')) {
        setHasIntent(true)
        setTeaser(false)
      }
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  useEffect(() => {
    if (done || hasIntent || !timeReady) return
    if (!scrollReady && revisits === 0) return
    const tryShow = () => {
      if (document.querySelector('[aria-modal="true"]') || builderOnScreen()) return false
      setTeaser(true)
      setDone(true)
      window.sessionStorage.setItem(SESSION_KEY, '1')
      return true
    }
    if (tryShow()) return
    const interval = window.setInterval(() => {
      if (tryShow()) window.clearInterval(interval)
    }, 2000)
    return () => window.clearInterval(interval)
  }, [done, hasIntent, timeReady, scrollReady, revisits])

  return {
    open,
    teaser,
    /** Customer tapped the corner button. */
    expand: () => {
      setTeaser(false)
      setOpen(true)
    },
    dismissTeaser: () => {
      setTeaser(false)
      snooze()
    },
    close: () => {
      setOpen(false)
      snooze()
    },
    // Reopened only on purpose, from "choose another" inside the recovery area.
    reopen: () => setOpen(true),
  }
}
